import { cn } from "@-/com.npmjs.cn";

/** A glyph as the landing names it: the character or component and its Haniverse mnemonic. */
export type LandingGlyph = {
  glyph: string;
  label: string;
};

/**
 * A square glyph tile with its mnemonic underneath, echoing the app's character tiles. `accent`
 * marks the character a formula builds. The glyph is tagged Simplified Chinese so browsers pick
 * mainland glyph forms for it.
 */
export function LandingGlyphTile({
  className,
  glyph,
  isAccent = false,
  label,
}: LandingGlyph & { className?: string; isAccent?: boolean }) {
  return (
    <span className={cn("inline-flex w-min flex-col items-center gap-1.5", className)}>
      <span
        className={cn(
          "grid aspect-square w-11 place-items-center rounded-xs border text-2xl leading-none text-black sm:w-12 sm:text-[1.7rem]",
          isAccent ? "border-website-accent/45 bg-website-accent/10" : "border-black/12 bg-white",
        )}
        lang="zh-Hans"
      >
        {glyph}
      </span>
      <span className="text-[0.7rem] font-medium whitespace-nowrap text-current/55 sm:text-xs">
        {label}
      </span>
    </span>
  );
}

/** A `+`, `=` or "vs" between glyph tiles, centered on the tiles rather than their labels. */
export function LandingGlyphOperator({ children }: { children: string }) {
  return (
    <span aria-hidden={true} className="pb-8 text-base font-medium text-current/30 sm:text-lg">
      {children}
    </span>
  );
}

/**
 * "These parts make this character": each part's tile joined by `+`, then the result's accent
 * tile. Screen readers get the same statement as one sentence.
 */
export function LandingGlyphFormula({
  className,
  parts,
  result,
}: {
  className?: string;
  parts: LandingGlyph[];
  result: LandingGlyph;
}) {
  const description = `${parts.map((part) => `${part.glyph} (${part.label})`).join(" plus ")} makes ${result.glyph} (${result.label})`;

  return (
    <div
      aria-label={description}
      className={cn("flex items-end gap-2 sm:gap-2.5", className)}
      role="img"
    >
      {parts.map((part, index) => (
        <span className="contents" key={`${part.glyph}-${part.label}`}>
          {index === 0 ? null : <LandingGlyphOperator>+</LandingGlyphOperator>}
          <LandingGlyphTile {...part} />
        </span>
      ))}
      <LandingGlyphOperator>=</LandingGlyphOperator>
      <LandingGlyphTile {...result} isAccent />
    </div>
  );
}
